import { faCircle, faCog, faDatabase, faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSubscription } from 'observable-hooks';
import { useMemo, useState } from 'react';
import type { ChangeEvent, MouseEvent, ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router-dom';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import Button, { RoundedButton } from '../../form/Button';
import Input, { INPUT_SIZE } from '../../form/Input';
import Select from '../../form/Select';
import { ModalDialog } from '../../modal/layout/ModalDialog';
import { ModalFooter } from '../../modal/layout/ModalFooter';
import { ModalHeader } from '../../modal/layout/ModalHeader';
import { NOTIFICATION_VARIANT, useNotifications } from '../../notifications/Notifications';
import Form from '../../reactForm/Form';
import { Layout, PlainGroupContainer } from '../../reactForm/Layout';
import { BasePage } from '../BasePage';
import { LoadingPage } from '../LoadingPage';
import { NotFoundPage } from '../NotFoundPage';
import type { GraphTimeWindow, RenderRuntimeDataServices } from './elements/types';
import { PreviewContextSummary } from './PreviewContextSummary';
import { PreviewDateRangePicker } from './PreviewDateRangePicker';
import { parseDefinitions } from './elements/renderHelpers';
import {
    REPORT_TEMPLATE_NAME_MAX_LENGTH,
    type ReportTemplateSettingsSubmitPayload,
    type ReportTemplateSettingsValues,
    useReportTemplateSettingsFormController,
} from './forms/useReportTemplateSettingsFormController';
import type { PreviewRenderContext } from './previewContextFactory';
import { ReportStructureController } from './reportStructureController';
import { usePreviewContextSelection } from './usePreviewContextSelection';
import { useRuntimeDataServices } from './runtime/dataServices/useRuntimeDataServices';
import {
    overviewUrl,
    type PreviewSelectorConfig,
    type ReportTemplateEditValues,
    useReportTemplate,
} from './utils';
import { Editor } from './Editor';
import { RuntimeDataServiceDebugPanel } from './RuntimeDataServiceDebugPanel';
import { useDraftPageMargin } from './useDraftPageMargin';

// Keep runtime diagnostics hidden by default in R1; enable via `?debugDataServices=1`.
const RUNTIME_DATA_SERVICE_DEBUG_QUERY_KEY = 'debugDataServices';

interface SettingsModalProps {
    open: boolean;
    onClose: () => void;
    onSave?: (event: MouseEvent<HTMLButtonElement>) => void;
    saveDisabled?: boolean;
    saveLabel?: string;
    title: string;
    children: ReactNode;
    className?: string;
}

function SettingsModal({
    open,
    onClose,
    onSave,
    saveDisabled = false,
    saveLabel = gettext('SAVE'),
    title,
    children,
    className,
}: SettingsModalProps): JSX.Element {
    return (
        <ModalDialog
            open={open}
            onClose={onClose}
            closeOnClickOutside={true}
            closeOnEsc={true}
            className={className}
        >
            <div className="relative flex flex-col py-6">
                <ModalHeader title={title} onClose={onClose} />
                <div className="space-y-4 px-6">{children}</div>
                {onSave ? (
                    <ModalFooter
                        onClose={onClose}
                        onSave={onSave}
                        saveDisabled={saveDisabled}
                        saveLabel={saveLabel}
                    />
                ) : (
                    <div className="mt-6 px-6">
                        <div className="flex justify-end">
                            <Button variant="neutral" onClick={onClose} type="button">
                                {gettext('CLOSE')}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </ModalDialog>
    );
}

interface LoadedEditPageProps {
    reportTemplate: ReportTemplateEditValues;
    update: (
        nextSettings: ReportTemplateSettingsSubmitPayload
    ) => Promise<ReportTemplateSettingsValues>;
    previewContext: PreviewRenderContext;
    previewDateRange: GraphTimeWindow;
    onPreviewDateRangeChange: (range: GraphTimeWindow) => void;
    previewSelectorConfig: PreviewSelectorConfig;
    onPreviewSelectionChange: (event: ChangeEvent<HTMLSelectElement>) => void;
    runtimeDataServices: RenderRuntimeDataServices;
    showRuntimeDataServiceDebug: boolean;
}

function LoadedEditPage({
    reportTemplate,
    update,
    previewContext,
    previewDateRange,
    onPreviewDateRangeChange,
    previewSelectorConfig,
    onPreviewSelectionChange,
    runtimeDataServices,
    showRuntimeDataServiceDebug,
}: LoadedEditPageProps): JSX.Element {
    const { addNotification } = useNotifications();
    const [templateSettingsOpen, setTemplateSettingsOpen] = useState(false);
    const [previewSettingsOpen, setPreviewSettingsOpen] = useState(false);
    const previewTimezone =
        previewContext.kind === 'project'
            ? previewContext.project.timezone
            : previewContext.measuringPoint.timezone;
    // Internal diagnostics surface; intentionally hidden unless enabled through the query string.
    const [debugPanelOpen, setDebugPanelOpen] = useState(false);

    const settingsFormController = useReportTemplateSettingsFormController({
        template: reportTemplate,
        onSubmitTemplate: update,
    });

    const {
        formState: { isDirty },
        control,
        watch,
    } = useForm({
        formControl: settingsFormController.rhfProps.formControl,
    });

    useUnsavedChangesGuard({
        shouldWarn: isDirty,
        includeSearchAndHash: false,
        warnOnPageUnload: true,
    });

    const structureController = useMemo(
        () => new ReportStructureController(settingsFormController.rhfProps),
        [settingsFormController]
    );

    useSubscription(settingsFormController.onSubmitSuccessful$, () => {
        setTemplateSettingsOpen(false);
        addNotification({
            message: gettext('SAVED_SUCCESSFULLY'),
            variant: NOTIFICATION_VARIANT.SUCCESS,
            scrollToTop: true,
        });
    });

    function handleSave(event: MouseEvent<HTMLButtonElement>): void {
        settingsFormController.triggerSubmit(event).catch(() => undefined);
    }

    const watchTemplateSetting = watch as <TName extends keyof ReportTemplateSettingsSubmitPayload>(
        name: TName
    ) => ReportTemplateSettingsSubmitPayload[TName];
    const templateName = watchTemplateSetting('name');
    const pageSize = watchTemplateSetting('pageSize');
    const pageOrientation = watchTemplateSetting('pageOrientation');
    const pageMarginUnit = watchTemplateSetting('pageMarginUnit');
    const pageMargin = useDraftPageMargin(watchTemplateSetting('pageMarginValue'), pageMarginUnit, {
        value: reportTemplate.pageMarginValue,
        unit: reportTemplate.pageMarginUnit,
    });
    const pageSettings = useMemo(
        () => ({ pageSize, pageOrientation, pageMargin }),
        [pageSize, pageOrientation, pageMargin]
    );

    return (
        <BasePage
            title={templateName}
            titleNode={
                <Controller
                    control={control}
                    name="name"
                    render={({ field, fieldState: { error } }) => (
                        <>
                            <Input
                                {...field}
                                aria-label={gettext('NAME')}
                                aria-invalid={Boolean(error)}
                                error={error}
                                maxLength={REPORT_TEMPLATE_NAME_MAX_LENGTH}
                                size={INPUT_SIZE.SMALL}
                                className="border-transparent bg-transparent px-0 text-2xl font-light focus:border-neutral-300 focus:bg-white"
                            />
                            {error?.message && (
                                <span className="text-error mt-1 block text-sm">
                                    {error.message}
                                </span>
                            )}
                        </>
                    )}
                />
            }
            besideTitle={
                <div className="ml-5 flex items-center gap-x-5">
                    <div className="flex items-center gap-x-3">
                        <RoundedButton
                            variant="icon"
                            color="primary"
                            onClick={() => setPreviewSettingsOpen(true)}
                            aria-label={gettext('PREVIEW_CONTEXT')}
                            type="button"
                        >
                            <FontAwesomeIcon icon={faEye} className="text-sm" />
                        </RoundedButton>
                        <RoundedButton
                            variant="icon"
                            color="primary"
                            onClick={() => setTemplateSettingsOpen(true)}
                            aria-label={gettext('SETTINGS')}
                            type="button"
                        >
                            <FontAwesomeIcon icon={faCog} className="text-sm" />
                        </RoundedButton>
                        {showRuntimeDataServiceDebug ? (
                            <RoundedButton
                                variant="icon"
                                color="primary"
                                onClick={() => setDebugPanelOpen(true)}
                                aria-label={gettext('DATA_SERVICES')}
                                type="button"
                                disabled={!runtimeDataServices}
                            >
                                <FontAwesomeIcon icon={faDatabase} className="text-sm" />
                            </RoundedButton>
                        ) : null}
                        <RoundedButton to={overviewUrl}>{gettext('OVERVIEW')}</RoundedButton>
                        <RoundedButton onClick={handleSave}>
                            {isDirty && (
                                <FontAwesomeIcon
                                    icon={faCircle}
                                    className="pr-2 text-orange-400"
                                    data-testid="report-builder-save-dirty-indicator"
                                />
                            )}
                            {gettext('SAVE')}
                        </RoundedButton>
                    </div>
                </div>
            }
        >
            <Controller
                control={control}
                name="structure"
                render={({ field: { value } }) => (
                    <Editor
                        structure={parseDefinitions(value, ['structure'])}
                        structureController={structureController}
                        pageSettings={pageSettings}
                        context={previewContext}
                        previewDateRange={previewDateRange}
                        runtimeDataServices={runtimeDataServices}
                    />
                )}
            />
            <PreviewContextSummary
                context={previewContext}
                range={previewDateRange}
                timezone={previewTimezone}
                onOpen={() => setPreviewSettingsOpen(true)}
            />
            <SettingsModal
                open={previewSettingsOpen}
                onClose={() => setPreviewSettingsOpen(false)}
                title={gettext('PREVIEW_CONTEXT')}
                className="w-[calc(100%-2rem)] max-w-xl overflow-visible"
            >
                <p className="text-sm text-gray-700">{previewSelectorConfig.description}</p>
                <div>
                    <label
                        htmlFor={previewSelectorConfig.inputId}
                        className="mb-1 block text-sm text-gray-700"
                    >
                        {previewSelectorConfig.label}
                    </label>
                    <Select
                        id={previewSelectorConfig.inputId}
                        value={previewSelectorConfig.value}
                        onChange={onPreviewSelectionChange}
                        data={previewSelectorConfig.options}
                    />
                </div>
                <PreviewDateRangePicker
                    range={previewDateRange}
                    timezone={previewTimezone}
                    onChange={onPreviewDateRangeChange}
                />
            </SettingsModal>
            <SettingsModal
                open={templateSettingsOpen}
                onClose={() => setTemplateSettingsOpen(false)}
                onSave={handleSave}
                title={gettext('TEMPLATE_SETTINGS')}
            >
                <Form formController={settingsFormController} showSaveButton={false}>
                    <Layout
                        formController={settingsFormController}
                        GroupContainer={PlainGroupContainer}
                    />
                </Form>
            </SettingsModal>
            {showRuntimeDataServiceDebug ? (
                <RuntimeDataServiceDebugPanel
                    open={debugPanelOpen}
                    onClose={() => setDebugPanelOpen(false)}
                    dataServices={runtimeDataServices}
                />
            ) : null}
        </BasePage>
    );
}

export function EditPage(): JSX.Element {
    const { id } = useParams<{ id: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { reportTemplate, loading, update } = useReportTemplate(id!);
    const reportTemplateEntityType = reportTemplate?.entityType ?? null;
    const {
        previewContextData,
        previewContext,
        previewContextLoading,
        previewContextError,
        previewSelection,
        previewDateRange,
        onPreviewDateRangeChange,
        previewSelectorConfig,
        assertPreviewOptionsAvailable,
        onPreviewSelectionChange,
    } = usePreviewContextSelection(searchParams, setSearchParams, reportTemplateEntityType);
    const runtimeDataServices = useRuntimeDataServices();
    const showRuntimeDataServiceDebug =
        searchParams.get(RUNTIME_DATA_SERVICE_DEBUG_QUERY_KEY) === '1';

    if (loading && !reportTemplate) {
        return <LoadingPage />;
    }

    if (!reportTemplate) {
        return <NotFoundPage />;
    }

    if (previewContextError) {
        throw previewContextError;
    }

    if (previewContextLoading) {
        return <LoadingPage />;
    }

    if (!previewContextData) {
        throw new Error('Missing preview context query result.');
    }

    assertPreviewOptionsAvailable();
    if (!previewSelection || !previewDateRange || !previewContext) {
        // Defaults for a missing or unknown URL selection are being written.
        return <LoadingPage />;
    }

    if (!previewSelectorConfig) {
        throw new Error('Missing preview selector configuration for report template entity type.');
    }

    return (
        <LoadedEditPage
            reportTemplate={reportTemplate}
            update={update}
            previewContext={previewContext}
            previewDateRange={previewDateRange}
            onPreviewDateRangeChange={onPreviewDateRangeChange}
            previewSelectorConfig={previewSelectorConfig}
            onPreviewSelectionChange={onPreviewSelectionChange}
            runtimeDataServices={runtimeDataServices}
            showRuntimeDataServiceDebug={showRuntimeDataServiceDebug}
        />
    );
}
